import type { ID } from '../../models';
import type { Coach } from '../../models/coach';
import type { Purchase } from '../../models/purchase';
import type { Subscription } from '../../models/subscription';
import type { WrappedTimestamp } from '../../providers/client-provider';

import { toObj } from '../../helpers/data';
import { CoachLinkStatus, type CoachLink } from '../../models/coach-link';
import { PlanStatus, PlanType, type CoachPlan, type Prompt, type PromptFieldIncluded } from '../../models/coach-plan';
import type { PlanPromptHistory } from '../../models/plan-prompt-history';

export async function isCoachAlreadyLinked(coachId: ID): Promise<boolean> {
  const { getClientProvider } = await import('./index');
  const { UserDatabase } = await import('../../db/user-database');

  const userDb = new UserDatabase({
    provider: getClientProvider(),
  });

  const currentUserId = await userDb.provider.getCurrentUserId();

  if (!currentUserId) {
    throw new Error('User is not authenticated');
  }

  const coachLinkDb = userDb.getCoachLinksDb(currentUserId);
  const link = await coachLinkDb.getObj(coachId);
  return link?.status === CoachLinkStatus.Active;
}

export async function getCoach(coachId: ID): Promise<Coach> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  return coachDb.get(coachId).then(toObj) as Promise<Coach>;
}

export async function getDefaultCoachId(): Promise<string | null> {
  const { getRemoteConfig, getString, fetchAndActivate } = await import('firebase/remote-config');
  const rc = getRemoteConfig();

  try {
    await fetchAndActivate(rc);
    const coachId = getString(rc, 'defaultAiCoachId');
    return coachId || null;
  } catch (error) {
    console.error('[campaign-human-coach] Failed to fetch default coach from remote config', error);
    return null;
  }
}

export async function getFreemiumPlan(coachId: ID): Promise<CoachPlan> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  return coachDb.getFreemiumPlan(coachId) as Promise<CoachPlan>;
}

export async function getDefaultFreemiumPlan(): Promise<CoachPlan> {
  const defaultCoachId = await getDefaultCoachId();
  if (!defaultCoachId) {
    throw new Error('Default coach ID is not set in remote config');
  }

  return getFreemiumPlan(defaultCoachId);
}

export async function getCoachPlans(coachId: ID, status?: PlanStatus): Promise<CoachPlan[]> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  let query = coachDb.getPlanDb(coachId).provider.query().orderBy('createdAt', 'desc');
  if (status) {
    query = query.where('status', '==', status);
  }

  return coachDb
    .getPlanDb(coachId)
    .list(query)
    .then((docs) => docs.map((doc) => toObj(doc) as CoachPlan));
}

export async function updatePlanStatus(coachId: ID, planId: ID, status: PlanStatus): Promise<void> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  const planDb = coachDb.getPlanDb(coachId);
  await planDb.update(planId, { status });
}

export async function getCoachRecurringPlans(coachId: ID): Promise<CoachPlan[]> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  const planDb = coachDb.getPlanDb(coachId);
  const query = planDb.provider
    .query()
    .where('planType', '==', PlanType.RECURRING)
    .where('status', '==', PlanStatus.ACTIVE);

  return planDb.list(query).then((docs) => docs.map((doc) => toObj(doc) as CoachPlan));
}

export async function getPlan(coachId: ID, planId: ID): Promise<{ coach: Coach; plan: CoachPlan }> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  return {
    coach: (await coachDb.get(coachId).then(toObj)) as Coach,
    plan: (await coachDb.getPlanDb(coachId).get(planId).then(toObj)) as CoachPlan,
  };
}

export async function getCoachOneOffPlans(coachId: ID): Promise<CoachPlan[]> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  const planDb = coachDb.getPlanDb(coachId);
  const query = planDb.provider.query().where('planType', '==', PlanType.ONE_OFF);

  return planDb.list(query).then((docs) => docs.map((doc) => toObj(doc) as CoachPlan));
}

export async function getPlanPurchases({
  coachId,
  planId,
  startAfter,
}: {
  coachId: ID;
  planId: ID;
  startAfter?: WrappedTimestamp;
}) {
  const { getClientProvider } = await import('./index');
  const { PurchaseDatabase } = await import('../../db/purchase-database');

  const purchaseDb = new PurchaseDatabase({
    provider: getClientProvider(),
  });

  let query = purchaseDb.provider
    .query()
    .where('coachId', '==', coachId)
    .where('planId', '==', planId)
    .limit(10)
    .orderBy('createdAt', 'desc');
  if (startAfter) {
    query = query.startAfter(startAfter);
  }

  return purchaseDb.list(query).then((docs) => docs.map(toObj) as Purchase[]);
}

export async function getPlanSubscriptions({
  coachId,
  planId,
  startAfter,
}: {
  coachId: ID;
  planId: ID;
  startAfter?: WrappedTimestamp;
}) {
  const { getClientProvider } = await import('./index');
  const { SubscriptionDatabase } = await import('../../db/subscription-database');

  const subscriptionDb = new SubscriptionDatabase({
    provider: getClientProvider(),
  });

  let query = subscriptionDb.provider
    .query()
    .where('coachId', '==', coachId)
    .where('planId', '==', planId)
    .limit(10)
    .orderBy('createdAt', 'desc');
  if (startAfter) {
    query = query.startAfter(startAfter);
  }

  return subscriptionDb.list(query).then((docs) => docs.map(toObj) as Subscription[]);
}

export async function getPlanPromptHistory(coachId: ID, planId: ID): Promise<PlanPromptHistory[]> {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  const planPromptHistoryDb = coachDb.getPlanDb(coachId).getPromptHistoryDb(planId);

  const query = planPromptHistoryDb.provider.query().orderBy('createdAt', 'desc');

  return planPromptHistoryDb.list(query).then((docs) => docs.map(toObj)) as Promise<PlanPromptHistory[]>;
}

export async function savePrompts(coachId: ID, planId: ID, prompts: Prompt[], includedFields: PromptFieldIncluded) {
  const { getClientProvider } = await import('./index');
  const { CoachDatabase } = await import('../../db/coach-database');

  const coachDb = new CoachDatabase({
    provider: getClientProvider(),
  });

  const planDb = coachDb.getPlanDb(coachId);
  await planDb.update(planId, { prompts, includedFields });
}

export async function getCurrentUserCoachLink(): Promise<CoachLink[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db/user-database');
  const { toObj } = await import('../../helpers/data');

  const userDb = new UserDatabase({
    provider: getClientProvider(),
  });
  const currentUserId = await userDb.provider.getCurrentUserId();
  if (!currentUserId) {
    return [];
  }

  const coachLinkDb = userDb.getCoachLinksDb(currentUserId);
  const query = coachLinkDb.provider.query().where('status', '==', CoachLinkStatus.Active);

  return coachLinkDb.list(query).then((docs) => docs.map(toObj) as CoachLink[]);
}
